import React, { createContext, useCallback, useMemo, useRef, useState } from "react";

export type NumericKeypadMode = "currency";

export type NumericKeypadQuickAction = {
  id: string;
  label: string;
  onPress: () => void;
};

export type NumericKeypadField = {
  id: string;
  label: string;
  mode: NumericKeypadMode;
  getCents: () => number;
  setCents: (cents: number) => void;
  onClear?: () => void;
  maxCents?: number;
  quickActions?: NumericKeypadQuickAction[];
};

type NumericKeypadContextValue = {
  activeFieldId: string | null;
  setActiveFieldId: (id: string | null) => void;
  registerField: (field: NumericKeypadField) => void;
  unregisterField: (fieldId: string) => void;
  getActiveField: () => NumericKeypadField | null;
};

export const NumericKeypadContext = createContext<NumericKeypadContextValue | null>(
  null
);

export function NumericKeypadProvider({ children }: { children: React.ReactNode }) {
  const fieldsRef = useRef(new Map<string, NumericKeypadField>());
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);

  const registerField = useCallback((field: NumericKeypadField) => {
    fieldsRef.current.set(field.id, field);
  }, []);

  const unregisterField = useCallback((fieldId: string) => {
    fieldsRef.current.delete(fieldId);
    setActiveFieldId((current) => (current === fieldId ? null : current));
  }, []);

  const getActiveField = useCallback(() => {
    if (!activeFieldId) return null;
    return fieldsRef.current.get(activeFieldId) ?? null;
  }, [activeFieldId]);

  const value = useMemo<NumericKeypadContextValue>(
    () => ({
      activeFieldId,
      setActiveFieldId,
      registerField,
      unregisterField,
      getActiveField,
    }),
    [activeFieldId, getActiveField, registerField, unregisterField]
  );

  return (
    <NumericKeypadContext.Provider value={value}>
      {children}
    </NumericKeypadContext.Provider>
  );
}
